import { Box } from '@chakra-ui/react'
import type { BoxProps } from '@chakra-ui/react'
import type { HandleProps } from '@xyflow/react'
import { Handle, Position } from '@xyflow/react'
import type { FC, ReactNode, RefObject } from 'react'
import { memo } from 'react'

import { type ButtonProps } from '@app/components/ui/button'
import { useColorMode, useColorModeValue } from '@app/components/ui/color-mode'
import useAreNodesSelected from '@app/hooks/useAreNodesSelected'
import useStoreCurrentUser from '@app/hooks/useStoreCurrentUser'
import { NON_SELECTED_DIMMING_OPACITY } from '@app/lib/globals'
import AiNodeMenu from '@app/pages/strategies/components/nodes/components/ai/aiNodeMenu'
import type { DomainNode } from '@app/types'

export const handleStyles = (canEdit, size = 18, colorMode = 'light') => ({
  opacity: canEdit ? '1' : '0',
  backgroundColor: colorMode === 'light' ? '#fff' : '#ddd',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: colorMode === 'light' ? '#cbd5e0' : '#eee',
  width: `${size}px`,
  height: `${size}px`
})

interface Props extends BoxProps {
  topHandle?: Omit<HandleProps, 'position'>
  bottomHandle?: Omit<HandleProps, 'position'>
  leftHandle?: Omit<HandleProps, 'position'>
  rightHandle?: Omit<HandleProps, 'position'>
  children: ReactNode

  nodeRef: RefObject<HTMLDivElement>
  node: DomainNode
}

const RfHandle = ({ node, type, position, aiBuilderMode, generatedHandleStyles }) => (
  <>
    <Handle type={type} position={position} style={generatedHandleStyles} id={position}></Handle>
    {aiBuilderMode && <AiNodeMenu node={node} position={position}></AiNodeMenu>}
  </>
)

const Node: FC<Props> = ({ topHandle, bottomHandle, leftHandle, rightHandle, children, nodeRef, node, ...rest }) => {
  const { user } = useStoreCurrentUser()
  const isEditor = ['admin', 'editor'].includes(user?.role)

  const { strategyId } = node
  const { colorMode } = useColorMode()
  const areNodesSelected = useAreNodesSelected(strategyId)
  const selectedNodeColor = useColorModeValue('blue.400', 'blue.600')

  const size = node?.metadata?.size || 'normal'
  const defaultMinW = 425
  const defaultMaxW = 425
  const minW = size === 'normal' ? `${defaultMinW}px` : `${defaultMinW * 1.3}px`
  const maxW = size === 'normal' ? `${defaultMaxW}px` : `${defaultMaxW * 1.3}px`
  const handleSize = size === 'normal' ? 18 : 24

  const { selected, metadata } = node
  const highlight = metadata?.highlight || false
  const dimNode = areNodesSelected && !selected && !highlight
  const opacity = dimNode ? NON_SELECTED_DIMMING_OPACITY : 1
  const aiBuilderMode = selected && isEditor

  let borderColor = 'transparent'
  let borderStyle = 'dashed'

  if (selected) {
    borderStyle = 'solid'
    borderColor = selectedNodeColor
  }
  const generatedHandleStyles: ButtonProps = handleStyles(isEditor, handleSize, colorMode)

  const renderHandle = (showHandle, type, position) =>
    showHandle && (
      <RfHandle
        node={node}
        aiBuilderMode={aiBuilderMode}
        type={type}
        position={position}
        generatedHandleStyles={generatedHandleStyles}
      />
    )

  return (
    <Box ref={nodeRef} minW={minW} maxW={maxW} {...rest}>
      {renderHandle(topHandle, topHandle?.type, Position.Top)}
      {renderHandle(leftHandle, leftHandle?.type, Position.Left)}
      <Box
        opacity={opacity}
        borderWidth="3px"
        borderStyle={borderStyle}
        borderColor={borderColor}
        borderRadius={node.type === 'commentThread' ? '32px 32px 32px 0' : '9px'}
        bgColor="transparent"
      >
        {children}
      </Box>
      {renderHandle(rightHandle, rightHandle?.type, Position.Right)}
      {renderHandle(bottomHandle, bottomHandle?.type, Position.Bottom)}
    </Box>
  )
}

export default memo(Node)
